import type { ZoneProfile } from '../services/zoneService';
import { readBitDepth, readSampleRate } from './audioQuality';
import { isUntestedDefaultZoneCap } from './zoneFormatCeiling';
import { MANUAL_LIMIT_KEY } from '../transport/formatCeilingEvidence';

export interface RendererRateCap {
  maxSampleRate: number;
  maxBitDepth: number;
}

type CapFields = Pick<ZoneProfile, 'max_sample_rate' | 'max_bit_depth'>;

const HI_RES_MAX_RATE = 192000;
const HI_RES_MAX_DEPTH = 24;

// Mirrors maestro-connector src-tauri/src/rate_cap.rs STANDARD_RATES.
const STANDARD_RATES = [44100, 48000, 88200, 96000, 176400, 192000, 352800, 384000];

/** The profile's limit when it actually binds Qobuz hi-res; null when there is nothing to enforce. */
export function bindingRateCap(profile: CapFields | null | undefined): RendererRateCap | null {
  const rate = Number(profile?.max_sample_rate);
  const depth = Number(profile?.max_bit_depth);
  if (!(rate > 0) || !(depth > 0)) return null;
  if (rate >= HI_RES_MAX_RATE && depth >= HI_RES_MAX_DEPTH) return null;
  return { maxSampleRate: rate, maxBitDepth: depth };
}

/**
 * Cap to restore from a stored profile. The untested 16/44 default is excluded:
 * it is a display ceiling, not a limit the user chose, and must not start
 * decimating renderers that were never capped.
 */
export function storedRateCap(profile: ZoneProfile | null | undefined): RendererRateCap | null {
  if (!profile) return null;
  if (hasManualLimitMarker(profile.test_results)) return bindingRateCap(profile);
  if (isUntestedDefaultZoneCap(profile)) return null;
  return bindingRateCap(profile);
}

/** True when test_results carry the Devices hand-set marker (#342 withManualLimitMarker). */
export function hasManualLimitMarker(results: Record<string, unknown> | null | undefined): boolean {
  const m = results?.[MANUAL_LIMIT_KEY] as { status?: string } | undefined;
  return !!m && typeof m === 'object' && m.status === 'manual';
}

/** Same choice the Connector makes: highest standard rate at or below the cap, preferring an integer ratio. */
export function deliveredRateHz(sourceHz: number, capHz: number): number {
  if (!(capHz > 0) || sourceHz <= capHz) return sourceHz;
  const below = STANDARD_RATES.filter((r) => r <= capHz);
  const sameFamily = below.filter((r) => Math.round(sourceHz) % r === 0);
  if (sameFamily.length) return Math.max(...sameFamily);
  return below.length ? Math.max(...below) : capHz;
}

export function sourceRateHz(track: {
  sampleRate?: number;
  sample_rate?: number;
  maximum_sampling_rate?: number;
  maxSampleRate?: number;
} | null | undefined): number | null {
  const v = track?.sampleRate ?? track?.sample_rate ?? track?.maximum_sampling_rate ?? track?.maxSampleRate;
  const n = Number(v);
  if (v == null || !Number.isFinite(n) || n <= 0) return null;
  return n >= 1000 ? Math.round(n) : Math.round(n * 1000);
}

/**
 * PCM rate/depth the renderer actually receives under the zone's limit.
 * The untested 16/44 default is display-only (never sent to the Connector), so it keeps its
 * original trigger (a track with its own sampleRate); a real cap applies to every track shape,
 * including Qobuz catalog fields.
 */
export function deliveredPcmFormat(
  track: Parameters<typeof sourceRateHz>[0] & Parameters<typeof readBitDepth>[0],
  profile: ZoneProfile,
): { effectiveRateKhz: number | null; effectiveDepth: number | null } {
  const bd = readBitDepth(track);
  const srKhz = readSampleRate(track);
  const hasOwnRate = !!(track?.sampleRate ?? track?.sample_rate);
  const srcHz = isUntestedDefaultZoneCap(profile) && !hasOwnRate ? null : sourceRateHz(track);
  const capHz = profile.max_sample_rate;
  return {
    effectiveRateKhz: srKhz && srcHz ? Math.round((capHz ? deliveredRateHz(srcHz, capHz) : srcHz) / 1000) : null,
    effectiveDepth: bd ? Math.min(bd, profile.max_bit_depth || bd) : null,
  };
}
